/**
 * useQuotaGate — checks resource quota for the current workspace.
 *
 * Wraps the `check_quota` Supabase RPC with a 30-second client-side cache
 * to avoid hammering the DB on every navigation.
 *
 * Usage:
 *   const { checkQuota } = useQuotaGate()
 *   const { used, maxAllowed, allowed } = await checkQuota('debate')
 */
import { hasSupabase, getSupabase } from '@/services/supabaseService';
import { useWorkspaceStore } from '@/stores/workspace';
import { captureEvent } from '@/boot/posthog';
import type { QuotaResource, QuotaState } from '@/types/billing';

const CACHE_TTL_MS = 30_000; // 30 seconds

// Module-level cache keyed by `workspaceId:resource`.
const cache = new Map<string, { result: QuotaState; expiresAt: number }>();

/**
 * Bumped by every `clearQuotaCache()`. A `check_quota` response is only written
 * to the cache when the epoch is unchanged since the call was issued.
 *
 * admin-vip-unlimited-access (review C1, residual window) — clearing on identity
 * change is a point-in-time purge, while `cache.set` runs AFTER its `await`. An
 * RPC issued by account A that resolves once B is signed in would otherwise
 * repopulate the cache with A's verdict, and B would read it with no network
 * call. Callers make this reachable by firing checkQuota with `.then()` rather
 * than awaiting it.
 */
let cacheEpoch = 0;

/**
 * Drops every cached quota verdict, for every workspace and every resource.
 *
 * admin-vip-unlimited-access (review C1) — the cache is keyed by
 * `workspaceId:resource` and NOT by the caller. `check_quota` however answers
 * for the CALLER (`auth.uid()`: the VIP predicate, and the `workspace`
 * resource's own `used` count), so two accounts sharing one workspace do not
 * share a verdict. Without this, in a single tab: account A (unlimited, owner)
 * fills the cache with `{ maxAllowed: 2147483647, allowed: true }`, signs out,
 * and account B — a plain member of the same workspace — signs in inside the
 * 30s window and reads A's verdict. `check_quota` is advisory (no SQL trigger
 * enforces it), so B would genuinely create past their own cap.
 *
 * Called from `stores/auth.ts` (setSession) on every identity change,
 * sign-out included — the same guard that calls `resetVipStatus()`. A token
 * refresh keeps the same identity and must NOT clear it: the verdicts are
 * still that account's own.
 */
export const clearQuotaCache = (): void => {
  cache.clear();
  cacheEpoch += 1;
};

/**
 * Composable for checking resource quotas against the current workspace.
 * Results are cached for 30 seconds per workspace+resource combination.
 */
export const useQuotaGate = () => {
  const workspaceStore = useWorkspaceStore();

  /**
   * Checks whether a given resource action is allowed for the current workspace.
   * Calls the Supabase `check_quota` RPC; falls back to `allowed: true` if
   * Supabase is unavailable (dev mode without backend).
   * @param resource - The resource type to check.
   * @returns QuotaState with `used`, `maxAllowed`, and `allowed` fields.
   */
  const checkQuota = async (resource: QuotaResource): Promise<QuotaState> => {
    const workspaceId = workspaceStore.workspaceId;
    if (!workspaceId) return { used: 0, maxAllowed: 0, allowed: true };

    const cacheKey = `${workspaceId}:${resource}`;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.result;
    }

    if (!hasSupabase()) {
      // Dev mode without backend — allow everything.
      return { used: 0, maxAllowed: 999, allowed: true };
    }

    // Snapshot the epoch: anything that clears the cache while this RPC is in
    // flight invalidates the answer we are about to receive.
    const epochAtCall = cacheEpoch;

    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .rpc('check_quota', { p_workspace_id: workspaceId, p_resource: resource })
        .single();

      if (error) {
        console.error('[useQuotaGate] RPC error:', error.message);
        return { used: 0, maxAllowed: 0, allowed: true };
      }

      const result: QuotaState = {
        used: (data as { used: number }).used ?? 0,
        maxAllowed: (data as { max_allowed: number }).max_allowed ?? 0,
        allowed: (data as { allowed: boolean }).allowed ?? true
      };

      // Drop the answer rather than cache it under another account.
      if (epochAtCall === cacheEpoch) {
        cache.set(cacheKey, { result, expiresAt: Date.now() + CACHE_TTL_MS });
      }

      // M11 — track quota hit event
      if (!result.allowed) {
        captureEvent('trial_quota_hit', { resource });
      }

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[useQuotaGate] Unexpected error:', msg);
      return { used: 0, maxAllowed: 0, allowed: true };
    }
  };

  /**
   * Invalidates the quota cache for a given resource in the current workspace.
   * Call this after successfully creating a resource (debate, persona, workspace).
   * @param resource - The resource type to invalidate.
   */
  const invalidateQuota = (resource: QuotaResource): void => {
    const workspaceId = workspaceStore.workspaceId;
    if (!workspaceId) return;
    cache.delete(`${workspaceId}:${resource}`);
  };

  return { checkQuota, invalidateQuota };
};
